import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { ServiceAboutText } from '@components/molecules/ServiceAboutText';
import { ServiceGallery } from '@components/molecules/ServiceGallery';
import { Footer } from '@components/molecules/Footer';
import { ServiceProfile, ServiceProfileProps } from '@components/molecules/ServiceProfile';
import { ProductsHighlightsSlider } from '@components/molecules/ProductsHighlightsSlider';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { Container } from '@components/atoms/Container';
import { Space } from '@components/atoms/Space';
import { ServicesGrid, ServicesGridProps } from '@components/molecules/ServicesGrid';
import { MenuPages } from '@types';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { getDynamicMenuItemValue } from '@utils/translation';

export interface MerchantDetailProps {
    uuid: string;
    name: string;
    logo?: ServiceProfileProps['logo'];
    email: string;
    phoneNumber: string;
    address: string[];
    locations?: {
        uuid: string;
        latitude: number;
        longitude: number;
    }[];
    services: ServicesGridProps['services'];
    schedule: { header?: string; text: string }[];
    bannerImage?: ServiceProfileProps['bannerImage'];
    website: string;
    type: string;
    hasProducts?: boolean;
    aboutText: string;
    images: string[];
    socialLinks: ServiceProfileProps['socialLinks'];
    productHighlights: ProductCardProps[];
    loading: boolean;
}

export const MerchantDetail: FC<MerchantDetailProps> = ({
    uuid,
    name,
    logo,
    email,
    phoneNumber,
    address,
    locations,
    services,
    schedule,
    bannerImage,
    website,
    type,
    hasProducts,
    aboutText,
    images,
    socialLinks,
    productHighlights,
    loading,
}) => {
    const { setStoreDetail, isLoadingStoreDetail, allSettings } = useData();
    const { t } = useTranslation();
    const [title, setTitle] = useState('');

    const translations = {
        title: title,
        galleryAlt: (index: number) => t('merchant-detail-component.gallery.alt', { index: `${index}`, name: `${name}` }),
        productHighlightsTitle: t('merchant-detail-component.product-highlights.title'),
        productHighlightsActionLabel: t('merchant-detail-component.product-highlights.action.label'),
    };

    useEffect(() => {
        setTitle(String(getDynamicMenuItemValue(allSettings?.data, 3)));
    }, [allSettings]);

    useEffect(() => {
        // when unmounting page, reset data
        // to prevent showing old results when returning to this page
        return () => {
            setStoreDetail(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const servicesRef = useRef<HTMLDivElement>(null);

    const [didInitialScroll, setDidInitialScroll] = useState<boolean>(false);

    // scroll to services
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('services') && services && servicesRef.current && !isLoadingStoreDetail && !didInitialScroll) {
            const headerHeight = document.querySelector('header')?.clientHeight || 0;
            const elementTop = servicesRef.current.getBoundingClientRect().top + window.pageYOffset;

            window.scrollTo({ top: elementTop - headerHeight, behavior: 'smooth' });
            setDidInitialScroll(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [services, isLoadingStoreDetail]);

    return (
        <>
            <StickyHeader menuActivePage={MenuPages.Merchants} />

            <Container>
                <>
                    {!loading && (
                        <BreadcrumbsBanner
                            title={translations.title}
                            links={[{ name: translations.title, url: 'merchants' }]}
                            currentPage={name}
                        />
                    )}

                    <Space spaceBreakpoints={{ xs: { space: 50 } }} />

                    {!loading && (
                        <>
                            <ServiceProfile
                                uuid={uuid}
                                name={name}
                                merchantName={name}
                                logo={logo}
                                email={email}
                                phoneNumber={phoneNumber}
                                address={address}
                                locations={locations}
                                schedule={schedule}
                                bannerImage={bannerImage}
                                socialLinks={socialLinks}
                                website={website}
                                type={type}
                                hasProducts={hasProducts || false}
                            />

                            {aboutText && (
                                <>
                                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                                    <ServiceAboutText content={aboutText} />
                                </>
                            )}

                            {services.length > 0 && (
                                <div ref={servicesRef}>
                                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                                    <ServicesGrid services={services} merchantName={name} />
                                </div>
                            )}

                            {images.length > 0 && (
                                <>
                                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                                    <ServiceGallery
                                        images={images.map((image, index) => {
                                            return { src: image, alt: translations.galleryAlt(index + 1) };
                                        })}
                                    />
                                </>
                            )}

                            {productHighlights.length > 0 && (
                                <>
                                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                                    <ProductsHighlightsSlider
                                        title={translations.productHighlightsTitle}
                                        actionLabel={translations.productHighlightsActionLabel}
                                        linkTo={`${SITE_URLS.products}?stores=${uuid}`}
                                        products={productHighlights}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            </Container>

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
};
